/*
 * Laptops and phones, in Access Control: the one-time codes a laptop enrolls
 * by, and switching off one that is lost.
 *
 * A code is shown once, as the server makes it, since the server keeps only
 * its hash. Switching off is enforced by the server, which refuses the device
 * everything, and by every anchor, which drops its keys and session at its
 * next heartbeat; this section only asks for it. App.js re-polls /nodes every
 * 3 s, so a device's row catches up on its own. Wording lives in
 * accessWords.js.
 *
 * ! file components/EnrolledDevices.js
 */
import React, { useState } from "react";
import { FiCopy, FiPlus, FiPower, FiSmartphone, FiTrash2 } from "react-icons/fi";
import { isMobileNode } from "../utils/nodeKind";
import * as W from "./accessWords";

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "never");

export default function EnrolledDevices({
  nodes,
  enrollments,
  domains,
  grantableDomains,
  send,
  busy,
  reload,
  toast,
  confirm,
}) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ label: "", domain: "" });
  const [made, setMade] = useState(null);

  const domain = form.domain || grantableDomains[0]?.id || "";
  const orgName = (id) => domains.find((d) => d.id === id)?.name || id || W.NO_ORG;
  const devices = nodes
    .filter(isMobileNode)
    .sort((a, b) => a.node_id.localeCompare(b.node_id));

  const make = async (e) => {
    e.preventDefault();
    try {
      const { data } = await send("/enrollments", "POST", { domain, label: form.label.trim() });
      setMade({ code: data.code, ...data.enrollment });
      setForm({ label: "", domain: "" });
      setShowNew(false);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(made.code);
      toast.success(W.COPIED);
    } catch {
      toast.error(W.COPY_FAILED);
    }
  };

  const withdraw = async (en) => {
    const ok = await confirm({
      title: `${W.withdrawCodeLabel(en.label)}?`,
      message: W.WITHDRAW_CODE_MEANS,
      confirmLabel: W.WITHDRAW,
      variant: "danger",
    });
    if (!ok) return;
    try {
      await send(`/enrollments/${encodeURIComponent(en.id)}`, "DELETE");
      toast.success(W.codeWithdrawn(en.label));
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const switchTo = async (n, off) => {
    if (off) {
      const ok = await confirm({
        title: `${W.switchOffLabel(n.node_id)}?`,
        message: W.SWITCH_OFF_MEANS,
        confirmLabel: W.SWITCH_OFF,
        variant: "danger",
      });
      if (!ok) return;
    }
    try {
      await send(`/nodes/${encodeURIComponent(n.node_id)}/${off ? "off" : "on"}`, "POST");
      toast.success(off ? W.switchedOff(n.node_id) : W.switchedOn(n.node_id));
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="panel">
      <div className="ac-head">
        <div>
          <div className="panel-title">
            <FiSmartphone style={{ verticalAlign: "-2px", marginRight: 8 }} />
            {W.DEVICES_TITLE}
          </div>
          <div className="panel-sub">{W.DEVICES_LEAD}</div>
        </div>
        <button
          type="button"
          className="ac-btn primary"
          onClick={() => setShowNew((v) => !v)}
          disabled={busy}
        >
          <FiPlus size={14} /> {showNew ? "Cancel" : W.MAKE_CODE}
        </button>
      </div>

      {showNew &&
        (grantableDomains.length === 0 ? (
          <div className="panel-note">{W.NO_ORGS}</div>
        ) : (
          <form onSubmit={make} className="ac-newform">
            <div className="ac-grid">
              <label className="ac-field">
                <span>{W.DEVICE_NAME}</span>
                <input
                  className="ac-input"
                  value={form.label}
                  maxLength={64}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder={W.DEVICE_NAME_EXAMPLE}
                  autoComplete="off"
                />
              </label>
              <label className="ac-field">
                <span>{W.ORGANISATION}</span>
                <select
                  className="ac-input"
                  value={domain}
                  onChange={(e) => setForm({ ...form, domain: e.target.value })}
                >
                  {grantableDomains.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name || d.id}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="ac-blurb">{W.DEVICE_NAME_WHY}</div>
            <div>
              <button
                type="submit"
                className="ac-btn primary"
                disabled={busy || !form.label.trim()}
              >
                {W.MAKE}
              </button>
            </div>
          </form>
        ))}

      {made && (
        <div className="ac-made" role="status">
          <div className="ac-made-label">{W.codeFor(made.label)}</div>
          <div className="ac-made-code">{made.code}</div>
          <div className="ac-blurb">{W.codeUse(fmtDate(made.expires))}</div>
          <div className="ac-row-actions">
            <button type="button" className="ac-btn" onClick={copy}>
              <FiCopy size={14} /> {W.COPY}
            </button>
            <button type="button" className="ac-btn" onClick={() => setMade(null)}>
              {W.DONE}
            </button>
          </div>
        </div>
      )}

      {enrollments.length > 0 && (
        <>
          <div className="ac-subhead">{W.CODES_WAITING}</div>
          <div className="ac-list">
            {enrollments.map((en) => (
              <div key={en.id} className="ac-row">
                <div className="ac-row-main">
                  <div className="ac-row-title">{en.label}</div>
                  <div className="ac-row-meta">
                    {W.codeMeta(en, orgName(en.domain), fmtDate(en.expires))}
                  </div>
                </div>
                <button
                  type="button"
                  className="ac-btn danger icon"
                  onClick={() => withdraw(en)}
                  disabled={busy}
                  title={W.withdrawCodeLabel(en.label)}
                  aria-label={W.withdrawCodeLabel(en.label)}
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="ac-subhead">{W.DEVICES_JOINED}</div>
      {devices.length === 0 ? (
        <div className="panel-note">{W.NO_DEVICES}</div>
      ) : (
        <div className="ac-list">
          {devices.map((n) => (
            <div key={n.node_id} className="ac-row">
              <div className="ac-row-main">
                <div className="ac-row-title">
                  {n.node_id}
                  {n.off && <span className="ac-tag off">{W.SWITCHED_OFF}</span>}
                </div>
                <div className="ac-row-meta">
                  {W.deviceMeta(n, orgName(n.domain), fmtDate(n.last_seen), fmtDate(n.user?.until))}
                </div>
              </div>
              {n.off ? (
                <button
                  type="button"
                  className="ac-btn"
                  onClick={() => switchTo(n, false)}
                  disabled={busy}
                  aria-label={W.switchOnLabel(n.node_id)}
                >
                  <FiPower size={14} /> {W.SWITCH_ON}
                </button>
              ) : (
                <button
                  type="button"
                  className="ac-btn danger"
                  onClick={() => switchTo(n, true)}
                  disabled={busy}
                  aria-label={W.switchOffLabel(n.node_id)}
                >
                  <FiPower size={14} /> {W.SWITCH_OFF}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
